import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Dumbbell, Home, TreePine } from 'lucide-react';
import { WorkoutFocus, Workout } from '@/types/workout';
import { workoutApi } from '@/services/workoutApi';
import { useWorkoutStore } from '@/lib/stores/workoutStore';
import { WorkoutGenerationCarousel } from './WorkoutGenerationCarousel';

interface GenerateWorkoutFlowProps {
  onComplete: (workout: Workout) => void;
  onCancel: () => void;
  onSwitchToManual?: () => void;
}

type Step = 'location' | 'duration' | 'feeling' | 'limitations' | 'prompt';
type WorkoutLocation = 'gym' | 'home' | 'calisthenics';

const durations = [5, 15, 30, 45, 60, 90];

const locations: { value: WorkoutLocation; label: string; description: string; icon: typeof Dumbbell }[] = [
  { value: 'gym', label: 'Gym', description: 'Full equipment access', icon: Dumbbell },
  { value: 'home', label: 'Home', description: 'Minimal or no equipment', icon: Home },
  { value: 'calisthenics', label: 'Outdoor', description: 'Calisthenics park or open space', icon: TreePine },
];

const feelings: { value: WorkoutFocus | 'energy' | 'stress-relief' | 'light'; label: string; description: string }[] = [
  { value: 'strength', label: 'Strong', description: 'Ready to lift heavy and push limits' },
  { value: 'energy', label: 'Energized', description: 'Full of energy, want to move fast' },
  { value: 'mobility', label: 'Tight', description: 'Need to stretch and move freely' },
  { value: 'stress-relief', label: 'Stressed', description: 'Need to blow off some steam' },
  { value: 'light', label: 'Light', description: 'Want something gentle and easy' },
  { value: 'core', label: 'Core-focused', description: 'Want to strengthen my center' },
];

const commonLimitations: { value: string; label: string }[] = [
  { value: 'lower-back', label: 'Lower back sensitivity' },
  { value: 'knee', label: 'Knee issues' },
  { value: 'shoulder', label: 'Shoulder problems' },
  { value: 'wrist', label: 'Wrist pain' },
  { value: 'neck', label: 'Neck tension' },
  { value: 'ankle', label: 'Ankle issues' },
];

export function GenerateWorkoutFlow({ onComplete, onCancel, onSwitchToManual }: GenerateWorkoutFlowProps) {
  const [step, setStep] = useState<Step>('location');
  const [params, setParams] = useState<{
    location?: WorkoutLocation;
    duration?: number;
    feeling?: string;
    limitations?: string[];
    prompt?: string;
  }>({
    limitations: [],
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [customDuration, setCustomDuration] = useState<string>('');
  const [customLimitation, setCustomLimitation] = useState<string>('');
  const { addGeneratedWorkout } = useWorkoutStore();

  const handleLocationSelect = (location: WorkoutLocation) => {
    setParams({ ...params, location });
    setStep('duration');
  };

  const handleDurationSelect = (duration: number) => {
    setParams({ ...params, duration });
    setShowCustomDuration(false);
    setCustomDuration('');
    setStep('feeling');
  };

  const handleCustomDurationSubmit = () => {
    const duration = parseInt(customDuration);
    if (duration > 0 && duration <= 180) {
      handleDurationSelect(duration);
    } else {
      setError('Please enter a valid duration between 1 and 180 minutes');
    }
  };

  const handleFeelingSelect = (feeling: string) => {
    setParams({ ...params, feeling });
    setStep('limitations');
  };

  const toggleLimitation = (limitation: string) => {
    setParams((prev) => {
      const current = prev.limitations || [];
      if (current.includes(limitation)) {
        return { ...prev, limitations: current.filter((l) => l !== limitation) };
      } else {
        return { ...prev, limitations: [...current, limitation] };
      }
    });
  };

  const addCustomLimitation = () => {
    if (customLimitation.trim()) {
      setParams((prev) => ({
        ...prev,
        limitations: [...(prev.limitations || []), customLimitation.trim()],
      }));
      setCustomLimitation('');
    }
  };

  const handleLimitationsContinue = () => {
    setStep('prompt');
  };

  const buildWorkoutPrompt = (): string => {
    const parts: string[] = ['Create a'];
    
    // Add duration
    if (params.duration) {
      parts.push(`${params.duration}-minute`);
    }
    
    // Add location context
    if (params.location === 'gym') {
      parts.push('gym workout with full equipment access');
    } else if (params.location === 'home') {
      parts.push('home workout with minimal or no equipment');
    } else if (params.location === 'calisthenics') {
      parts.push('outdoor calisthenics workout using bodyweight exercises');
    } else {
      parts.push('workout');
    }
    
    // Add feeling/focus
    if (params.feeling) {
      if (params.feeling === 'energy') {
        parts.push('that is high-energy and dynamic');
      } else if (params.feeling === 'stress-relief') {
        parts.push('focused on stress relief and intense movement');
      } else if (params.feeling === 'light') {
        parts.push('that is light and gentle');
      } else {
        parts.push(`focusing on ${params.feeling}`);
      }
    }
    
    // Add limitations
    if (params.limitations && params.limitations.length > 0) {
      parts.push(`. Important: Avoid exercises that stress these areas: ${params.limitations.join(', ')}`);
    }
    
    // Add custom prompt if provided
    if (params.prompt && params.prompt.trim()) {
      parts.push(`. Additional instructions: ${params.prompt.trim()}`);
    }
    
    return parts.join(' ');
  };

  const handleGenerate = async () => {
    if (!params.duration || !params.location) {
      setError('Please complete all required steps');
      return;
    }

    setIsGenerating(true);
    setError(null);
    
    try {
      const prompt = buildWorkoutPrompt();
      const workout = await workoutApi.generateWorkout(prompt);
      
      // Don't save to store here - let the user decide to save or start
      // The workout will be saved when they choose an action
      
      setIsGenerating(false);
      onComplete(workout);
    } catch (error) {
      console.error('Failed to generate workout:', error);
      setIsGenerating(false);
      setError('Failed to generate workout. Please try again.');
    }
  };

  const handleBack = () => {
    if (step === 'duration') setStep('location');
    else if (step === 'feeling') setStep('duration');
    else if (step === 'limitations') setStep('feeling');
    else if (step === 'prompt') setStep('limitations');
  };

  const getStepNumber = () => {
    switch (step) {
      case 'location': return 1;
      case 'duration': return 2;
      case 'feeling': return 3;
      case 'limitations': return 4;
      case 'prompt': return 5;
      default: return 1;
    }
  };

  const getProgressWidth = () => {
    return `${(getStepNumber() / 5) * 100}%`;
  };

  return (
    <>
      <WorkoutGenerationCarousel isVisible={isGenerating} />
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-6">
        <div className="w-full max-w-2xl">
        {/* Progress indicator */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-neutral-500">Step {getStepNumber()} of 5</span>
            {onSwitchToManual && (
              <button
                onClick={onSwitchToManual}
                className="text-sm text-neutral-600 hover:text-neutral-900 underline transition-colors"
              >
                Switch to Manual Mode
              </button>
            )}
          </div>
          <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-neutral-900"
              initial={{ width: 0 }}
              animate={{ width: getProgressWidth() }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Location */}
          {step === 'location' && (
            <motion.div
              key="location"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-4xl mb-3 text-neutral-900">
                Where are you working out?
              </h2>
              <p className="text-neutral-600 mb-12">
                This helps us choose the right exercises for you.
              </p>

              <div className="space-y-3">
                {locations.map((location) => {
                  const Icon = location.icon;
                  return (
                    <button
                      key={location.value}
                      onClick={() => handleLocationSelect(location.value)}
                      className="w-full py-6 px-6 bg-white border border-neutral-200 rounded-lg hover:border-neutral-900 hover:bg-neutral-50 transition-all text-left flex items-center gap-4"
                    >
                      <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-neutral-700" />
                      </div>
                      <div>
                        <div className="text-xl mb-1 text-neutral-900">{location.label}</div>
                        <div className="text-neutral-500">{location.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 2: Duration */}
          {step === 'duration' && (
            <motion.div
              key="duration"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-4xl mb-3 text-neutral-900">
                How long do you have?
              </h2>
              <p className="text-neutral-600 mb-12">
                Choose a duration that fits your schedule.
              </p>

              {!showCustomDuration ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {durations.map((duration) => (
                      <button
                        key={duration}
                        onClick={() => handleDurationSelect(duration)}
                        className="py-8 px-6 bg-white border border-neutral-200 rounded-lg hover:border-neutral-900 hover:bg-neutral-50 transition-all text-center"
                      >
                        <div className="text-3xl mb-1 text-neutral-900">{duration}</div>
                        <div className="text-neutral-500">min</div>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setShowCustomDuration(true)}
                    className="w-full py-6 px-6 bg-white border border-neutral-200 rounded-lg hover:border-neutral-900 hover:bg-neutral-50 transition-all text-center"
                  >
                    <div className="text-xl text-neutral-900">Custom</div>
                  </button>
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Enter duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="180"
                      value={customDuration}
                      onChange={(e) => {
                        setCustomDuration(e.target.value);
                        setError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCustomDurationSubmit();
                        }
                      }}
                      placeholder="e.g., 45"
                      className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent text-neutral-900 text-lg"
                      autoFocus
                    />
                    {error && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleCustomDurationSubmit}
                      className="flex-1 py-4 px-6 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-all text-lg font-medium"
                    >
                      Continue
                    </button>
                    <button
                      onClick={() => {
                        setShowCustomDuration(false);
                        setCustomDuration('');
                        setError(null);
                      }}
                      className="px-6 py-4 bg-white border border-neutral-200 rounded-lg hover:border-neutral-900 hover:bg-neutral-50 transition-all text-neutral-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Feeling */}
          {step === 'feeling' && (
            <motion.div
              key="feeling"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-4xl mb-3 text-neutral-900">
                What feels right today?
              </h2>
              <p className="text-neutral-600 mb-12">
                Select how you're feeling to personalize your workout.
              </p>

              <div className="grid grid-cols-2 gap-3">
                {feelings.map((feeling) => (
                  <button
                    key={feeling.value}
                    onClick={() => handleFeelingSelect(feeling.value)}
                    className="py-5 px-5 bg-white border border-neutral-200 rounded-lg hover:border-neutral-900 hover:bg-neutral-50 transition-all text-left"
                  >
                    <div className="text-lg mb-1 text-neutral-900">{feeling.label}</div>
                    <div className="text-sm text-neutral-500">{feeling.description}</div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => handleFeelingSelect('')}
                className="mt-6 text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                Skip — surprise me
              </button>
            </motion.div>
          )}

          {/* Step 4: Limitations */}
          {step === 'limitations' && (
            <motion.div
              key="limitations"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-4xl mb-3 text-neutral-900">
                Any physical limitations?
              </h2>
              <p className="text-neutral-600 mb-8">
                Select any areas we should be mindful of. This helps create a safer workout with appropriate warmup and mobility work.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {commonLimitations.map((limitation) => {
                  const isSelected = params.limitations?.includes(limitation.value);
                  return (
                    <button
                      key={limitation.value}
                      onClick={() => toggleLimitation(limitation.value)}
                      className={`py-4 px-5 border rounded-lg transition-all text-left ${
                        isSelected
                          ? 'bg-neutral-900 text-white border-neutral-900'
                          : 'bg-white border-neutral-200 text-neutral-900 hover:border-neutral-900 hover:bg-neutral-50'
                      }`}
                    >
                      {limitation.label}
                    </button>
                  );
                })}
              </div>

              {/* Custom limitation input */}
              <div className="flex gap-2 mb-8">
                <input
                  type="text"
                  value={customLimitation}
                  onChange={(e) => setCustomLimitation(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addCustomLimitation();
                    }
                  }}
                  placeholder="Add other limitation..."
                  className="flex-1 px-4 py-3 bg-white border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent text-neutral-900"
                />
                <button
                  onClick={addCustomLimitation}
                  className="px-4 py-3 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-all"
                >
                  Add
                </button>
              </div>

              {/* Selected custom limitations */}
              {params.limitations && params.limitations.filter(l => !commonLimitations.some(cl => cl.value === l)).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {params.limitations
                    .filter(l => !commonLimitations.some(cl => cl.value === l))
                    .map((limitation) => (
                      <span
                        key={limitation}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-900 text-white rounded-full text-sm"
                      >
                        {limitation}
                        <button
                          onClick={() => toggleLimitation(limitation)}
                          className="ml-1 hover:text-neutral-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              )}

              <button
                onClick={handleLimitationsContinue}
                className="w-full py-5 px-6 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-all text-lg font-medium"
              >
                {params.limitations && params.limitations.length > 0 ? 'Continue' : 'No limitations — Continue'}
              </button>
            </motion.div>
          )}

          {/* Step 5: Additional Prompt */}
          {step === 'prompt' && (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-4xl mb-3 text-neutral-900">
                Anything else?
              </h2>
              <p className="text-neutral-600 mb-8">
                Add any specific requests or guidance for your workout (optional).
              </p>

              {/* Summary of selections */}
              <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-6">
                <div className="text-sm text-neutral-500 mb-2">Your workout preferences:</div>
                <div className="flex flex-wrap gap-2">
                  {params.location && (
                    <span className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm">
                      {locations.find(l => l.value === params.location)?.label}
                    </span>
                  )}
                  {params.duration && (
                    <span className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm">
                      {params.duration} min
                    </span>
                  )}
                  {params.feeling && (
                    <span className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm">
                      {feelings.find(f => f.value === params.feeling)?.label || params.feeling}
                    </span>
                  )}
                  {params.limitations && params.limitations.length > 0 && (
                    <span className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm">
                      {params.limitations.length} limitation{params.limitations.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              <textarea
                value={params.prompt || ''}
                onChange={(e) => setParams({ ...params, prompt: e.target.value })}
                placeholder="e.g., include more compound movements, focus on posterior chain, add a finisher at the end..."
                className="w-full min-h-[120px] px-4 py-3 bg-white border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent resize-none text-neutral-900 placeholder:text-neutral-400"
              />

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full mt-6 py-5 px-6 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-all text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? 'Generating...' : 'Generate Workout'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12">
          <button
            onClick={step === 'location' ? onCancel : handleBack}
            disabled={isGenerating}
            className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 'location' ? 'Cancel' : 'Back'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

